import { useState } from 'react';
import { useV2 } from './ctx';
import { Sheet, Field, Effect, MoneyInput } from './ui';
import { rp, shortDate, dayOf, amtOf, sgnOf } from './model';

// Ubah baris: jumlah, tanggal and truk of one line in one sheet, sent with
// one reason (Aurel, via the Director, 30 Oct: "can she do ubah jumlah, ubah
// tanggal etc. all in one go instead of having to save it one by one?").
// One line = one packet = one reason: the Director sees it in Perubahan as
// one row and can drill into why ("she changed the value AND the date AND
// the truck for this line. and then we can drill into why"). Not a tray of
// several lines under one shared reason: that would blur the why.
// Dragging a line onto another day (Daftar, Kalender) opens this with the
// new date filled in.
const TRIP = ['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan', 'Balikan'];

// what the sheet changes, against the line as it stands
function changesOf(line, d) {
  const out = [];
  if (d.amount != null && d.amount !== amtOf(line)) out.push({ k: 'jumlah', from: rp(amtOf(line)), to: rp(d.amount) });
  if (d.date && d.date !== dayOf(line.tanggal)) out.push({ k: 'tanggal', from: shortDate(line.tanggal), to: shortDate(d.date) });
  if (d.truck && d.truck !== String(line.no_truk || '')) out.push({ k: 'truk', from: line.no_truk || '—', to: d.truck });
  return out;
}

export function UbahBarisSheet({ line, date: preDate, onClose }) {
  const { api, user, planId, monthKey, done, role, model } = useV2();
  const cur = amtOf(line);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(preDate || dayOf(line.tanggal));
  const [truck, setTruck] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const trip = !!line.no_truk || TRIP.includes(line.tipe);
  const n = parseInt(amount || '0', 10);
  const next = { amount: amount && n > 0 && n !== cur ? n : undefined, date: date && date !== dayOf(line.tanggal) ? date : undefined, truck: truck.trim() && truck.trim() !== String(line.no_truk || '') ? truck.trim() : undefined };
  const badDate = next.date && next.date.slice(0, 7) !== monthKey;
  const badTruck = next.truck && !/^\d{3,5}$/.test(next.truck);
  const ch = changesOf(line, next);
  const send = async () => {
    setBusy(true);
    const base = { planId, planLineId: line.id, monthKey, requestedBy: user.name };
    // a truck swap applies at once (as Ganti truk always has); the reason names it so the Director's packet shows the whole change
    const why = reason.trim() + (next.truck ? ` · truk ${line.no_truk || '—'} → ${next.truck}` : '');
    try {
      if (next.truck) await api.changeTruck(line.id, next.truck, monthKey, reason.trim());
      if (next.amount != null) await api.createAmendment({ ...base, reason: why, amendmentType: 'amount_change', originalValue: `Rp ${cur.toLocaleString('id-ID')}`, requestedValue: `Rp ${next.amount.toLocaleString('id-ID')}` });
      if (next.date) await api.createAmendment({ ...base, reason: why, amendmentType: 'date_change', originalValue: dayOf(line.tanggal), requestedValue: next.date });
      await done(`${line.keterangan} dikirim${role === 'director' || (!next.amount && !next.date) ? '' : ' · menunggu Direktur'}`);
    } catch (e) { setBusy(false); alert(e.message); }
  };
  return (
    <Sheet title="Ubah baris" onClose={onClose}>
      <div className="fld">
        <Field label="Baris"><span className="t" style={{ textAlign: 'right' }}>{line.keterangan}</span></Field>
        <Field label="Sekarang"><span className="amt">{rp(cur)} · {shortDate(line.tanggal)}{line.no_truk ? ` · ${line.no_truk}` : ''}</span></Field>
      </div>
      <div className="fld">
        <Field label="Jumlah baru"><MoneyInput value={amount} onChange={setAmount} /></Field>
        <Field label="Tanggal"><input type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>
        {trip && <Field label="Truk"><input inputMode="numeric" placeholder={line.no_truk || 'tetap'} value={truck} onChange={e => setTruck(e.target.value)} /></Field>}
      </div>
      <div className="fld"><Field label="Alasan"><input autoFocus={!!preDate} placeholder="wajib" value={reason} onChange={e => setReason(e.target.value)} /></Field></div>
      {badDate && <div className="note warn">Tanggal di luar bulan ini belum bisa.</div>}
      {badTruck && <div className="note warn">Nomor truk 3–5 angka.</div>}
      {next.amount != null && <Effect label="Saldo akhir bulan kalau disetujui" from={model.settledEnd} to={model.settledEnd + sgnOf(line) * (n - cur)} />}
      <div className="note info">{ch.length ? `${ch.map(c => `${c.k} ${c.from} → ${c.to}`).join(' · ')}.` : 'Belum ada yang berubah.'}{next.truck ? ' Truk langsung tersimpan.' : ''}{next.amount != null || next.date ? ` ${next.truck ? 'Sisanya' : 'Perubahan ini'} menunggu persetujuan Direktur, sebagai satu baris dengan alasan ini.` : ''}</div>
      <button type="button" className="btn" disabled={busy || !ch.length || badDate || badTruck || !reason.trim()} onClick={send}>{busy ? 'Mengirim…' : 'Kirim'}</button>
    </Sheet>
  );
}
